import React, { useState, useCallback, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { changeObjectPropertyAction } from 'redux/actions'
import { initialDebtInformation } from 'redux/reducers/appReducer'
import FieldInput from 'shared/components/FieldInput'
import PaymentAmount from './PaymentAmount'
import { validatePaymentAmountErrors, validateFileNumberErrors, validateFileNumberVINErrors } from 'shared/helpers/validators'

const REDUX_APP_PROPERTY = 'debtInformation'
const DebtInformationForm = ({ t, i18n, editDebtInfoId, setValidateForm, setIsError }) => {
  const dispatch = useDispatch()
  const [formErrors, setFormErrors] = useState(initialDebtInformation)

  const debtInformationList = useSelector((state) => state.app.debtInformationList)
  const { fileNumber, isVehicleTaxDebt, paymentAmount } = useSelector((state) => state.app.debtInformation)

  const handleChangeObjectPropertyAction = useCallback(
    (fieldName) => (value) => {
      dispatch(changeObjectPropertyAction(REDUX_APP_PROPERTY, fieldName, value))
    },
    [dispatch]
  )

  const handleVINChangeAction = useCallback(
    (value) => {
      const replaced = value.replace(/I/i, 1).replace(/Q/i, '').replace(/O/i, 0)
      dispatch(changeObjectPropertyAction(REDUX_APP_PROPERTY, 'fileNumber', replaced))
    },
    [dispatch]
  )

  const validateFileNumber = useCallback(
    (fileNumber) => {
      const newFormErrors = { ...formErrors }
      newFormErrors.fileNumber = validateFileNumberErrors(fileNumber)
      setFormErrors(newFormErrors)
    },
    [formErrors]
  )

  const validateFileNumberVIN = useCallback(
    (fileNumber) => {
      const newFormErrors = { ...formErrors }
      newFormErrors.fileNumber = validateFileNumberVINErrors(fileNumber)
      setFormErrors(newFormErrors)
    },
    [formErrors]
  )

  const validatePaymentAmount = useCallback((paymentAmountStr) => {
    const paymentAmountErrors = validatePaymentAmountErrors(paymentAmountStr)
    setFormErrors((prevFormErrors) => ({ ...prevFormErrors, paymentAmount: paymentAmountErrors }))
  }, [])

  const validateFields = useCallback(() => {
    const newFormErrors = {}

    newFormErrors.fileNumber = isVehicleTaxDebt ? validateFileNumberVINErrors(fileNumber) : validateFileNumberErrors(fileNumber)

    if (!editDebtInfoId) {
      const foundDup = debtInformationList.find((debtInfo) => fileNumber === debtInfo.fileNumber)
      if (foundDup) {
        newFormErrors.fileNumber = isVehicleTaxDebt ? 'errors.file_number_vin_duplicate' : 'errors.file_number_duplicate'
      }
    }

    newFormErrors.paymentAmount = validatePaymentAmountErrors(paymentAmount)

    const isValid = Object.values(newFormErrors).every((val) => !val)
    const isFormClean = Object.values(newFormErrors).some((val) => val === '')

    setFormErrors(newFormErrors)

    const newDebtInfo = { fileNumber, paymentAmount, isVehicleTaxDebt }

    return !isFormClean && isValid ? newDebtInfo : false
  }, [fileNumber, paymentAmount, isVehicleTaxDebt, debtInformationList])

  useEffect(() => {
    setValidateForm(() => validateFields)
  }, [setValidateForm, validateFields])

  return (
    <div className="row">
      <div className="col-xs-12 col-md-8">
        {/* VIN Number */}
        {isVehicleTaxDebt && (
          <FieldInput
            id="FileNumberVIN"
            name="FileNumberVIN"
            type="text"
            customClasses={'fw-20'}
            aria-describedby="vin"
            autoComplete="off"
            maxLength="17"
            minLength="1"
            label={t('debt_info.vin')}
            t={t}
            value={fileNumber}
            validate={validateFileNumberVIN}
            errorMsg={formErrors.fileNumber}
            isValid={formErrors.fileNumber === '' ? '' : !formErrors.fileNumber}
            setIsError={setIsError}
            helperText={t('debt_info.vin_note')}
            changeObjectPropertyAction={handleVINChangeAction}
          />
        )}

        {/* File Number */}
        {!isVehicleTaxDebt && (
          <FieldInput
            id="FileNumber"
            name="FileNumber"
            type="text"
            customClasses={'fw-20'}
            aria-describedby="file-number"
            autoComplete="off"
            maxLength="6"
            minLength="1"
            label={t('debt_info.file_number')}
            t={t}
            value={fileNumber}
            helperText={t('debt_info.file_number_helper')}
            validate={validateFileNumber}
            errorMsg={formErrors.fileNumber}
            isValid={formErrors.fileNumber === '' ? '' : !formErrors.fileNumber}
            setIsError={setIsError}
            changeObjectPropertyAction={handleChangeObjectPropertyAction('fileNumber')}
          />
        )}

        {/* Payment amount */}
        <PaymentAmount
          validate={validatePaymentAmount}
          error={formErrors.paymentAmount}
          label={t('debt_info.payment_amount')}
          isValid={formErrors.paymentAmount === '' ? '' : !formErrors.paymentAmount}
          setIsError={setIsError}
          amount={paymentAmount}
          reduxAppProperty={REDUX_APP_PROPERTY}
          changeObjectPropertyAction={handleChangeObjectPropertyAction('paymentAmount')}
          // id="PaymentAmount"
          // name="PaymentAmount"
          // type="number"
          // customClasses={'fw-20'}
          // aria-describedby="payment-amount"
          // autoComplete="off"
          // maxLength="9"
          // label={t('debt_info.payment_amount')}
          // t={t}
          // value={paymentAmount}
          // validate={validatePaymentAmount}
          // errorMsg={formErrors.paymentAmount}
          // isValid={formErrors.paymentAmount === '' ? '' : !formErrors.paymentAmount}
          // setIsError={setIsError}
          // changeObjectPropertyAction={handlePaymentAmount}
        />
      </div>
    </div>
  )
}

export default withTranslation('common')(DebtInformationForm)
